import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings2, Check, X, Sparkles, Search } from "lucide-react";


// Dropdown con búsqueda por código o nombre. Útil con 488 cuentas del plan NIIF.
function CuentaSearchSelect({ value, onChange, cuentas, placeholder = "Seleccionar cuenta...", testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = cuentas.find((c) => c.id === value);
  const filtered = useMemo(() => {
    if (!q.trim()) return cuentas.slice(0, 50);
    const qn = q.toLowerCase();
    return cuentas.filter((c) => c.codigo.toLowerCase().includes(qn) || c.nombre.toLowerCase().includes(qn)).slice(0, 80);
  }, [q, cuentas]);

  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 h-9 px-3 border border-[#c7d3cc] rounded-md bg-white text-sm hover:border-[#205c45]"
      >
        <span className="truncate text-left">
          {selected ? <><span className="font-mono text-[#5c7368]">{selected.codigo}</span> · {selected.nombre}</> : <span className="text-[#8ba397]">{placeholder}</span>}
        </span>
        <Search size={14} className="text-[#8ba397]" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#c7d3cc] rounded-md shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-[#e9f2ee]">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Código o nombre..." className="h-8 text-sm" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && <div className="px-3 py-4 text-xs text-[#8ba397]">Sin resultados</div>}
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#e9f2ee] flex gap-2 ${c.id === value ? "bg-[#f7f9f8] font-medium" : ""}`}
              >
                <span className="font-mono text-[#5c7368] w-20 shrink-0">{c.codigo}</span>
                <span className="truncate flex-1">{c.nombre}</span>
                {c.es_auxiliar && <span className="text-[9px] text-[#205c45] shrink-0">aux</span>}
              </button>
            ))}
          </div>
          <div className="p-2 border-t border-[#e9f2ee] flex justify-between text-[10px] text-[#8ba397]">
            <span>{filtered.length} / {cuentas.length} cuentas</span>
            <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="text-[#b53a25] hover:underline">Limpiar</button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AccountingConfig() {
  const [tipos, setTipos] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [edit, setEdit] = useState(null); // tipo_movimiento key
  const [form, setForm] = useState({});
  const [applying, setApplying] = useState(false);

  const load = async () => {
    try {
      const [t, c, cc] = await Promise.all([
        api.get("/config-contable/tipos"),
        api.get("/config-contable"),
        api.get("/accounting/cuentas"),
      ]);
      setTipos(t.data);
      setConfigs(c.data);
      setCuentas(cc.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const configByTipo = Object.fromEntries(configs.map(c => [c.tipo_movimiento, c]));

  const openEdit = (tipo) => {
    const existing = configByTipo[tipo.key];
    setForm({
      tipo_movimiento: tipo.key,
      cuenta_debe_id: existing?.cuenta_debe_id || "",
      cuenta_haber_id: existing?.cuenta_haber_id || "",
      cuenta_impuesto_id: existing?.cuenta_impuesto_id || "",
      cuenta_auxiliar_id: existing?.cuenta_auxiliar_id || "",
      activo: existing?.activo ?? true,
    });
    setEdit(tipo);
  };

  const save = async () => {
    if (!form.cuenta_debe_id || !form.cuenta_haber_id) {
      return toast.error("Las cuentas Debe y Haber son obligatorias");
    }
    try {
      await api.post("/config-contable", {
        ...form,
        cuenta_impuesto_id: form.cuenta_impuesto_id || null,
        cuenta_auxiliar_id: form.cuenta_auxiliar_id || null,
      });
      toast.success(`Configuración guardada para "${edit.label}"`);
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (tipo) => {
    if (!window.confirm(`¿Quitar configuración de "${tipo.label}"? Los asientos automáticos dejarán de generarse para este flujo.`)) return;
    try {
      await api.delete(`/config-contable/${tipo.key}`);
      toast.success("Configuración eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cuentaLabel = (id) => {
    const c = cuentas.find(x => x.id === id);
    return c ? `${c.codigo} · ${c.nombre}` : "—";
  };

  const autoAplicar = async () => {
    if (!window.confirm("¿Aplicar configuración sugerida NIIF Ecuador a las 7 categorías?\n\nSolo se completarán los campos vacíos; no se sobrescribirá lo ya configurado manualmente.")) return;
    setApplying(true);
    try {
      const { data } = await api.post("/config-contable/auto-aplicar");
      toast.success(`${data.total} configuraciones aplicadas: ${data.aplicados.join(", ")}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  return (
    <div className="space-y-6" data-testid="config-contable-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad · Configuración</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Asientos contables automáticos</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Define qué cuentas del plan se usan al generar asientos automáticos por cada tipo de movimiento. Si no hay configuración, el asiento automático se omite (pero la transacción continúa).</p>
        </div>
        <Button
          onClick={autoAplicar}
          disabled={applying || cuentas.length === 0}
          className="bg-[#b07d2a] hover:bg-[#8a5e1e] text-white shrink-0"
          data-testid="auto-aplicar-btn"
        >
          <Sparkles size={14} className="mr-1.5" />
          {applying ? "Aplicando..." : "Aplicar NIIF Ecuador"}
        </Button>
      </div>

      {cuentas.length === 0 && (
        <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-3 text-xs text-[#8a5500]">
          ⚠️ No hay cuentas contables definidas. Ve a <strong>Contabilidad → Plan de cuentas</strong> y crea las cuentas antes de configurar los automatismos.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tipos.map(t => {
          const cfg = configByTipo[t.key];
          const configurado = !!cfg;
          return (
            <div key={t.key} className="border border-[#d1dbd6] bg-white rounded-md p-4" data-testid={`config-card-${t.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${configurado ? "bg-[#205c45]" : "bg-[#c7d3cc]"}`} />
                    <span className="font-semibold text-[#15241d]">{t.label}</span>
                  </div>
                  <p className="text-xs text-[#5c7368] mt-1 italic">{t.hint}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-${t.key}`}>
                    <Settings2 size={14} className="mr-1" />Configurar
                  </Button>
                  {configurado && (
                    <Button size="sm" variant="outline" onClick={() => remove(t)} className="text-[#7a2514]" data-testid={`remove-${t.key}`}>
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
              {configurado && (
                <div className="mt-3 text-xs space-y-1 bg-[#e9f2ee] border border-[#b2d0c2] rounded p-2">
                  <div><span className="text-[#5c7368] uppercase tracking-wider font-semibold">Debe:</span> {cfg.cuenta_debe_id_label || cuentaLabel(cfg.cuenta_debe_id)}</div>
                  <div><span className="text-[#5c7368] uppercase tracking-wider font-semibold">Haber:</span> {cfg.cuenta_haber_id_label || cuentaLabel(cfg.cuenta_haber_id)}</div>
                  {cfg.cuenta_impuesto_id && <div><span className="text-[#5c7368] uppercase tracking-wider font-semibold">Impuesto:</span> {cfg.cuenta_impuesto_id_label || cuentaLabel(cfg.cuenta_impuesto_id)}</div>}
                  {cfg.cuenta_auxiliar_id && <div><span className="text-[#5c7368] uppercase tracking-wider font-semibold">Auxiliar:</span> {cfg.cuenta_auxiliar_id_label || cuentaLabel(cfg.cuenta_auxiliar_id)}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{edit?.label}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#5c7368] italic">{edit?.hint}</p>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Cuenta Debe *</Label>
              <CuentaSearchSelect
                value={form.cuenta_debe_id}
                onChange={(v) => setForm((f) => ({ ...f, cuenta_debe_id: v }))}
                cuentas={cuentas}
                testid="sel-debe"
              />
            </div>
            <div>
              <Label>Cuenta Haber *</Label>
              <CuentaSearchSelect
                value={form.cuenta_haber_id}
                onChange={(v) => setForm((f) => ({ ...f, cuenta_haber_id: v }))}
                cuentas={cuentas}
                testid="sel-haber"
              />
            </div>
            <div>
              <Label>Cuenta Impuesto (opcional) <span className="text-[10px] text-[#8ba397]">— IVA por pagar / IVA crédito tributario</span></Label>
              <CuentaSearchSelect
                value={form.cuenta_impuesto_id}
                onChange={(v) => setForm((f) => ({ ...f, cuenta_impuesto_id: v }))}
                cuentas={cuentas}
                placeholder="— (sin cuenta de impuesto)"
                testid="sel-impuesto"
              />
            </div>
            <div>
              <Label>Cuenta Auxiliar (opcional) <span className="text-[10px] text-[#8ba397]">— p.ej. segunda retención separada</span></Label>
              <CuentaSearchSelect
                value={form.cuenta_auxiliar_id}
                onChange={(v) => setForm((f) => ({ ...f, cuenta_auxiliar_id: v }))}
                cuentas={cuentas}
                placeholder="— (sin cuenta auxiliar)"
                testid="sel-auxiliar"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="save-config"><Check size={14} className="mr-1" />Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
